import React from 'react'
import { UserOutlined, LockOutlined, CreditCardOutlined, SafetyOutlined, WhatsAppOutlined, KeyOutlined, ShopOutlined } from '@ant-design/icons';
import { Menu } from 'antd';
import { Link, Route, Navigate, useLocation, Routes, useParams } from 'react-router-dom';
import InnerAppLayout from 'layouts/inner-app-layout';
import EditProfile from './EditProfile';
import BusinessProfile from './BusinessProfile';
import ChangePassword from './ChangePassword';
import Billing from './Billing';
import UserPermission from './Permissions';
import MetaCredential from './MetaCredential';
import { useMyContext } from 'Context/MyContextProvider';
import Security from './Security';
import AdminSetting from './AdminSetting';
import apiClient from 'services/apiClient';
import { usePermission, PermissionProtected } from 'views/app-views/whatsapp/Hooks/usePermissionGuard';

const url = '/users/manage'
const UserSetting = () => {
	const { api, authToken, navigate, UserData, themeData, userRole } = useMyContext();
	const { id } = useParams();
	const [editedUserRole, setEditedUserRole] = React.useState(null);

	React.useEffect(() => {
		if (id && api && authToken) {
			apiClient.get(`${api}edit-user/${id}`, {
				headers: {
					'Authorization': 'Bearer ' + authToken,
				}
			})
				.then(response => {
					const roleName = response.data?.user?.[0]?.role?.name;
					setEditedUserRole(roleName);
				})
				.catch(err => console.error(err));
		}
	}, [id, api, authToken]);

	const isViewer = (editedUserRole === 'Viewer') || (userRole === 'Viewer');

	const location = useLocation();
	const locationPath = location.pathname.split('/');
	const currentpath = locationPath[locationPath.length - 1];
	const selectedKey = (!currentpath || currentpath === id) ? 'edit-profile' : currentpath;

	const titleMap = {
		'edit-profile': 'Edit User',
		'business-profile': 'Business Profile',
		'change-password': 'Change Password',
		'billing': 'Billing',
		'permissions': 'Permissions',
		'credential': 'Meta Credential',
		'security': 'Security'
	};
	const currentTitle = titleMap[selectedKey] || 'Edit User';

	return (
		<InnerAppLayout
			title={currentTitle}
			sideContentWidth={320}
			closable={false}
			sideContent={<SettingOption id={id} isViewer={isViewer} />}
			mainContent={<SettingContent themeData={themeData} id={id} api={api} authToken={authToken} navigate={navigate} UserData={UserData} isViewer={isViewer} />}
		/>
	);
}
const MenuItem = ({ icon, path, label }) => {
	return (
		<>
			{icon}
			<span>{label}</span>
			<Link to={`${url}/${path}`} />
		</>
	);
};

const SettingOption = ({ id, isViewer }) => {
	const location = useLocation();
	const locationPath = location.pathname.split('/');
	const currentpath = locationPath[locationPath.length - 1];
	const selectedKey = (!currentpath || currentpath === id) ? 'edit-profile' : currentpath;

	const canViewProfile = usePermission('View Edit Profile');
	const canChangePassword = usePermission('Change Password');
	const canViewBilling = usePermission('View Billing');
	const canViewPermission = usePermission('View User Permission');
	const canViewCredential = usePermission('View Meta Credential');
	const canViewSecurity = usePermission('View User Security');

	return (
		<Menu
			mode="inline"
			selectedKeys={[selectedKey]}
		>
			{canViewProfile && (
				<Menu.Item key="edit-profile" icon={<UserOutlined />}><MenuItem label="Edit Profile" path={`${id}/edit-profile`} /></Menu.Item>
			)}

			{canViewProfile && !isViewer && (
				<Menu.Item key="business-profile" icon={<ShopOutlined />}><MenuItem label="Business Profile" path={`${id}/business-profile`} /></Menu.Item>
			)}

			{canChangePassword && (
				<Menu.Item key="change-password" icon={<LockOutlined />}><MenuItem label="Change Password" path={`${id}/change-password`} /></Menu.Item>
			)}

			{/* internal is not completed  */}
			{canViewBilling && !isViewer && (
				<Menu.Item key="billing" icon={<CreditCardOutlined />}><MenuItem label="Billing" path={`${id}/billing`} /></Menu.Item>
			)}

			{canViewPermission && (
				<Menu.Item key="permissions" icon={<SafetyOutlined />}><MenuItem label="Permissions" path={`${id}/permissions`} /></Menu.Item>
			)}

			{canViewCredential && !isViewer && (
				<Menu.Item key="credential" icon={<WhatsAppOutlined />}><MenuItem label="Meta Credential" path={`${id}/credential`} /></Menu.Item>
			)}

			{canViewSecurity && (
				<Menu.Item key="security" icon={<KeyOutlined />}><MenuItem label="Security" path={`${id}/security`} /></Menu.Item>
			)}
		</Menu>
	);
};

const SettingContent = ({ id, api, authToken, navigate, UserData, themeData, isViewer }) => {
	return (
		<Routes>
			<Route path={`/`} exact element={
				<PermissionProtected permission="View Edit Profile">
					<EditProfile UserData={UserData} id={id} api={api} authToken={authToken} navigate={navigate} />
				</PermissionProtected>
			} />
			<Route path={`/edit-profile`} exact element={
				<PermissionProtected permission="View Edit Profile">
					<EditProfile UserData={UserData} id={id} api={api} authToken={authToken} navigate={navigate} />
				</PermissionProtected>
			} />
			{!isViewer && (
				<Route path={`/business-profile`} exact element={
					<PermissionProtected permission="View Edit Profile">
						<BusinessProfile UserData={UserData} id={id} api={api} authToken={authToken} navigate={navigate} />
					</PermissionProtected>
				} />
			)}
			<Route path={`/change-password`} exact element={
				<PermissionProtected permission="Change Password">
					<ChangePassword UserData={UserData} id={id} api={api} authToken={authToken} navigate={navigate} />
				</PermissionProtected>
			} />
			<Route path={`/billing`} exact element={
				<PermissionProtected permission="View Billing">
					<Billing themeData={themeData} UserData={UserData} id={id} api={api} authToken={authToken} navigate={navigate} />
				</PermissionProtected>
			} />
			<Route path={`/permissions`} exact element={
				<PermissionProtected permission="View User Permission">
					<UserPermission UserData={UserData} id={id} api={api} authToken={authToken} navigate={navigate} />
				</PermissionProtected>
			} />
			<Route path={`/credential`} exact element={
				<PermissionProtected permission="View Meta Credential">
					<MetaCredential UserData={UserData} id={id} api={api} authToken={authToken} navigate={navigate} />
				</PermissionProtected>
			} />
			<Route path={`/security`} exact element={
				<PermissionProtected permission="View User Security">
					<Security UserData={UserData} id={id} api={api} authToken={authToken} navigate={navigate} />
				</PermissionProtected>
			} />
			<Route path={`/setting`} exact element={
				<PermissionProtected permission="View System Setting">
					<AdminSetting UserData={UserData} id={id} api={api} authToken={authToken} navigate={navigate} />
				</PermissionProtected>
			} />
			<Route path={`/*`} exact element={<Navigate to={`edit-profile`} UserData={UserData} id={id} api={api} authToken={authToken} navigate={navigate} />} />
		</Routes>
	);
};

export default UserSetting;
